// PartnerStatusDrillDown — Team N (2026-05-11)
//
// 業者別学習状況 dashboard の drill-down side panel。
// 選択 partner の templates / curated / deal samples / proposals / receipts /
// audits / drifts / outcomes / timeline を表示。

import React, { useEffect, useState, useCallback } from 'react';
import {
  CardBase,
  Note,
  Button,
  MessageBlock,
  SubSectionTitle,
  InlineSpinner,
} from '@freee_jp/vibes';
import {
  MdClose,
  MdSchool,
  MdTrendingUp,
  MdHistory,
  MdReceiptLong,
  MdRule,
  MdInfo,
  MdStar,
  MdCompareArrows,
  MdInsights,
  MdSyncProblem,
  MdCheckCircle,
  MdWarning,
} from 'react-icons/md';
import {
  fetchPartnerLearningDetail,
  fetchLearningIntegrityCheck,
  PartnerLearningDetail,
  LearningIntegrityResponse,
} from '../../api/client';

const fmtPct = (n: number | null | undefined): string =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`;

const fmtYen = (n: number | null | undefined): string =>
  n == null ? '—' : `¥${Number(n).toLocaleString()}`;

const fmtDate = (s: string | null | undefined): string =>
  s ? String(s).slice(0, 19).replace('T', ' ') : '—';

export interface PartnerStatusDrillDownProps {
  partnerId: number;
  onClose: () => void;
}

export const PartnerStatusDrillDown: React.FC<PartnerStatusDrillDownProps> = ({
  partnerId,
  onClose,
}) => {
  const [detail, setDetail] = useState<PartnerLearningDetail | null>(null);
  const [integrity, setIntegrity] = useState<LearningIntegrityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, ic] = await Promise.all([
        fetchPartnerLearningDetail(partnerId),
        fetchLearningIntegrityCheck(partnerId).catch(() => null),
      ]);
      setDetail(d);
      setIntegrity(ic);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setLoading(false);
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  if (!partnerId) return null;

  return (
    <CardBase paddingSize="small" mb={0.5}>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <SubSectionTitle headlineLevel={3}>
              {detail?.partner?.partner_name || `partner_${partnerId}`} 詳細
            </SubSectionTitle>
            <Note mt={0.25}>partner_id: {partnerId}</Note>
          </div>
          <Button onClick={onClose} small>
            <MdClose style={{ verticalAlign: 'middle', marginRight: 4 }} />
            閉じる
          </Button>
        </div>

        {loading && <InlineSpinner isLoading={true} />}
        {error && <MessageBlock error mt={0.5}>{error}</MessageBlock>}

        {detail && (
          <>
            {/* 概況 row */}
            <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
              <Kpi
                icon={<MdStar />}
                label="登録 status"
                value={detail.partner?.pattern_status || '—'}
                sub={detail.partner?.first_registered_at ? `since ${fmtDate(detail.partner.first_registered_at)}` : ''}
                color="#7c5cff"
              />
              <Kpi
                icon={<MdSchool />}
                label="template 数"
                value={String(detail.templates.length)}
                sub={detail.templates.filter(t => t.template_status === 'promoted').length > 0 ? `promoted ${detail.templates.filter(t => t.template_status === 'promoted').length}` : 'draft only'}
                color="#338650"
              />
              <Kpi
                icon={<MdTrendingUp />}
                label="cadence 信頼度"
                value={fmtPct(detail.cadence?.confidence)}
                sub={detail.cadence?.sample_size ? `sample ${detail.cadence.sample_size}` : ''}
                color="#2e7cf6"
              />
              <Kpi
                icon={<MdReceiptLong />}
                label="過去 deal"
                value={String(detail.deal_samples.length)}
                sub={`(最新 ${detail.deal_samples.length} 件)`}
                color="#6b6560"
              />
              <Kpi
                icon={<MdInfo />}
                label="prefill 採用率"
                value={(() => {
                  const e = detail.outcomes.filter(o => o.prefill_emitted === 1).length;
                  const u = detail.outcomes.filter(o => o.prefill_used === 1).length;
                  return e > 0 ? fmtPct(u / e) : '—';
                })()}
                sub={`outcome ${detail.outcomes.length} 件`}
                color="#338650"
              />
            </div>

            {/* Templates */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>Layout Templates</SubSectionTitle>
              {detail.templates.length === 0 ? (
                <Note mt={0.25}>未学習 (= partner_invoice_layout_templates row 不在)</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>id</th>
                        <th style={th}>status</th>
                        <th style={th}>fingerprint</th>
                        <th style={th}>producer</th>
                        <th style={th}>paradigm</th>
                        <th style={{ ...th, textAlign: 'right' }}>sample</th>
                        <th style={{ ...th, textAlign: 'right' }}>approved</th>
                        <th style={{ ...th, textAlign: 'right' }}>rejected</th>
                        <th style={{ ...th, textAlign: 'right' }}>conf</th>
                        <th style={th}>promoted_at</th>
                        <th style={th}>actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.templates.map(t => (
                        <tr key={t.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={td}>{t.id}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: t.template_status === 'promoted' ? '#338650' :
                                   t.template_status === 'demoted' ? '#c33939' : '#7c5cff' }}>
                            {t.template_status}
                          </td>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.6875rem' }}>
                            {t.layout_fingerprint ? String(t.layout_fingerprint).slice(0, 12) : '—'}
                          </td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{t.pdf_producer || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{t.amount_paradigm || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{t.sample_count}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#338650' }}>{t.approved_count}</td>
                          <td style={{ ...td, textAlign: 'right', color: t.rejected_count > 0 ? '#c33939' : undefined }}>{t.rejected_count}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtPct(t.ai_confidence)}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(t.promoted_at)}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>
                            <TemplateActionButtons templateId={t.id} status={t.template_status} onChange={load} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Curated patterns */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>Curated Patterns (= partner-patterns.md)</SubSectionTitle>
              {!detail.curated ? (
                <Note mt={0.25}>partner-patterns.md 未記録</Note>
              ) : (
                <table style={{ width: '100%', marginTop: '4px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <tbody>
                    <Row label="account_item" value={`${detail.curated.default_account_item_name || '—'} (${detail.curated.default_account_item_id || '—'})`} />
                    <Row label="section" value={`${detail.curated.default_section_name || '—'} (${detail.curated.default_section_id || '—'})`} />
                    <Row label="item" value={`${detail.curated.default_item_name || '—'} (${detail.curated.default_item_id || '—'})`} />
                    <Row label="tax_code" value={detail.curated.default_tax_code} />
                    <Row label="amount_pattern" value={detail.curated.amount_pattern || '—'} />
                    <Row label="amount range" value={
                      detail.curated.amount_min != null || detail.curated.amount_max != null ?
                      `${fmtYen(detail.curated.amount_min)} 〜 ${fmtYen(detail.curated.amount_max)} (median ${fmtYen(detail.curated.amount_median)})` :
                      '—'
                    } />
                    <Row label="due offset" value={detail.curated.due_date_offset_days != null ? `${detail.curated.due_date_offset_days} 日` : '—'} />
                  </tbody>
                </table>
              )}
            </div>

            {/* Amount Paradigm Learning (Team M 統合) */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>金額抽出 Paradigm Learning</SubSectionTitle>
              <table style={{ width: '100%', marginTop: '4px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <tbody>
                  <Row label="preferred paradigm" value={detail.paradigm_learning.preferred_paradigm || '—'} />
                  <Row label="PDF label correct (= prefill_match)" value={detail.paradigm_learning.pdf_label_correct} highlight="#338650" />
                  <Row label="detail sum correct (= Team M 3-way 結果)" value={detail.paradigm_learning.detail_sum_correct} />
                  <Row label="mismatch count" value={detail.paradigm_learning.mismatch_count} highlight={detail.paradigm_learning.mismatch_count > 2 ? '#dd7a00' : undefined} />
                  <Row label="confidence" value={fmtPct(detail.paradigm_learning.confidence)} highlight="#7c5cff" />
                  <Row label="sample size 合計" value={detail.paradigm_learning.sample_size} />
                </tbody>
              </table>
            </div>

            {/* 過去 freee deal sample */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>過去 freee deal 明細 (上位 {detail.deal_samples.length} 件)</SubSectionTitle>
              {detail.deal_samples.length === 0 ? (
                <Note mt={0.25}>partner_details_cache に row なし。 freee sync を実行してください。</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>issue_date</th>
                        <th style={th}>deal_id</th>
                        <th style={{ ...th, textAlign: 'right' }}>amount</th>
                        <th style={th}>account_item</th>
                        <th style={th}>section</th>
                        <th style={{ ...th, textAlign: 'right' }}>tax</th>
                        <th style={th}>description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.deal_samples.map(d => (
                        <tr key={d.deal_id} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={td}>{d.issue_date || '—'}</td>
                          <td style={td}>{d.deal_id}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtYen(d.amount)}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{d.account_item_name || d.account_item_id || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{d.section_long_name || d.section_id || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{d.tax_code || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {d.description || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 最近 proposal */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>最近 proposal (deal_proposals, 直近 {detail.recent_proposals.length} 件)</SubSectionTitle>
              {detail.recent_proposals.length === 0 ? (
                <Note mt={0.25}>該当なし</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>ref_number</th>
                        <th style={th}>status</th>
                        <th style={th}>issue_date</th>
                        <th style={th}>due_date</th>
                        <th style={{ ...th, textAlign: 'right' }}>amount</th>
                        <th style={th}>freee_deal</th>
                        <th style={th}>proposed_at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.recent_proposals.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.6875rem' }}>{p.ref_number}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: p.status === 'registered' ? '#338650' :
                                   p.status === 'rejected' ? '#c33939' :
                                   p.status === 'approved' ? '#2e7cf6' : '#7c5cff' }}>
                            {p.status}
                          </td>
                          <td style={td}>{p.issue_date || '—'}</td>
                          <td style={td}>{p.due_date || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtYen(p.amount)}</td>
                          <td style={td}>{p.freee_deal_id || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(p.proposed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 最近 receipt */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>最近 receipt (arica_receipt_tracking, 直近 {detail.recent_receipts.length} 件)</SubSectionTitle>
              {detail.recent_receipts.length === 0 ? (
                <Note mt={0.25}>該当なし</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>arica_rowid</th>
                        <th style={th}>receive_date</th>
                        <th style={th}>status</th>
                        <th style={{ ...th, textAlign: 'right' }}>amount_pdf</th>
                        <th style={th}>issue_date_pdf</th>
                        <th style={th}>document_type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.recent_receipts.map(r => (
                        <tr key={r.arica_rowid} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={td}>{r.arica_rowid}</td>
                          <td style={td}>{r.receive_date}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: r.processing_status === 'registered' ? '#338650' :
                                   r.processing_status === 'rejected' ? '#c33939' : '#7c5cff' }}>
                            {r.processing_status}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.amount_pdf)}</td>
                          <td style={td}>{r.issue_date_pdf || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{r.document_type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 最近 audit */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>経理観点監査 (= invoice_accounting_audit, 直近 {detail.recent_audits.length} 件)</SubSectionTitle>
              {detail.recent_audits.length === 0 ? (
                <Note mt={0.25}>該当なし</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>arica_rowid</th>
                        <th style={th}>result</th>
                        <th style={{ ...th, textAlign: 'right' }}>score</th>
                        <th style={th}>category</th>
                        <th style={{ ...th, textAlign: 'right' }}>account</th>
                        <th style={{ ...th, textAlign: 'right' }}>tax</th>
                        <th style={th}>split?</th>
                        <th style={th}>audited_at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.recent_audits.map(a => (
                        <tr key={a.arica_rowid} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={td}>{a.arica_rowid}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: a.audit_result === 'pass' ? '#338650' :
                                   a.audit_result === 'fail' ? '#c33939' :
                                   a.audit_result === 'warn' ? '#dd7a00' : '#8a8580' }}>
                            {a.audit_result}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtPct(a.audit_score)}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{a.partner_category || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{a.expected_account_item_id || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{a.expected_tax_code || '—'}</td>
                          <td style={td}>{a.multi_store_split_required ? '要' : '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(a.audited_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Phase 4-B: Integrity Check */}
            {integrity && (
              <div style={{ marginTop: '16px' }}>
                <SubSectionTitle headlineLevel={4}>
                  <MdSyncProblem style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  学習結果 vs 過去 deal canonical Integrity 整合性
                </SubSectionTitle>
                <Note mt={0.25}>
                  verdict: <strong style={{
                    color: integrity.verdict === 'consistent' ? '#338650' :
                           integrity.verdict === 'inconsistent_warn' ? '#c33939' : '#dd7a00',
                  }}>{integrity.verdict}</strong> ({integrity.total_issues} 件 mismatch)
                </Note>
                {integrity.integrity_issues.length === 0 ? (
                  <div style={{ marginTop: '4px', color: '#338650', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MdCheckCircle /> 学習結果と過去 deal canonical は一致しています
                  </div>
                ) : (
                  <table style={{ width: '100%', marginTop: '4px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>severity</th>
                        <th style={th}>field</th>
                        <th style={th}>learned</th>
                        <th style={th}>deal_canonical</th>
                        <th style={{ ...th, textAlign: 'right' }}>deal_freq</th>
                      </tr>
                    </thead>
                    <tbody>
                      {integrity.integrity_issues.map((is, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={{ ...td, fontWeight: 600,
                            color: is.severity === 'warn' ? '#c33939' : '#dd7a00' }}>
                            {is.severity === 'warn' ? <MdWarning style={{ verticalAlign: 'middle' }} /> : <MdInfo style={{ verticalAlign: 'middle' }} />}
                            {is.severity}
                          </td>
                          <td style={{ ...td, fontWeight: 600 }}>{is.field}</td>
                          <td style={td}>{is.learned_name || is.learned || '—'} ({is.learned || '—'})</td>
                          <td style={td}>{is.deal_canonical_name || is.deal_canonical || '—'} ({is.deal_canonical || '—'})</td>
                          <td style={{ ...td, textAlign: 'right' }}>{is.deal_frequency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Phase 4-B: Hook 発火 統計 */}
            {detail.hook_stats && detail.hook_stats.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <SubSectionTitle headlineLevel={4}>
                  <MdInsights style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  学習 hook 発火統計 (= partner 単位)
                </SubSectionTitle>
                <table style={{ width: '100%', marginTop: '4px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                      <th style={th}>event_type</th>
                      <th style={th}>source_path</th>
                      <th style={{ ...th, textAlign: 'right' }}>count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.hook_stats.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                        <td style={{ ...td, fontWeight: 600,
                          color: s.event_type.includes('paradigm') || s.event_type.includes('canonical') ? '#7c5cff' :
                                 s.event_type.includes('split_learn') || s.event_type.includes('auto_debit') || s.event_type.includes('3way_to_bank') ? '#dd7a00' :
                                 s.event_type === 'past_deal_autofill' ? '#338650' :
                                 '#6b6560' }}>
                          {s.event_type}
                        </td>
                        <td style={{ ...td, fontSize: '0.6875rem' }}>{s.source_path || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{s.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Phase 4-B: Conflicts */}
            {detail.conflicts && detail.conflicts.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <SubSectionTitle headlineLevel={4}>
                  <MdCompareArrows style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  学習 conflict 履歴 ({detail.conflicts.length} 件)
                </SubSectionTitle>
                <Note mt={0.25}>異なる hook が異なる結論を出したケース</Note>
                <table style={{ width: '100%', marginTop: '4px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                      <th style={th}>detected_at</th>
                      <th style={th}>field</th>
                      <th style={th}>A source / value</th>
                      <th style={th}>B source / value</th>
                      <th style={th}>resolution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.conflicts.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                        <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(c.detected_at)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{c.conflict_field}</td>
                        <td style={td}>{c.hook_a_source}: {c.hook_a_value || '—'}</td>
                        <td style={td}>{c.hook_b_source}: {c.hook_b_value || '—'}</td>
                        <td style={{ ...td, fontWeight: 600, color: c.resolution ? '#338650' : '#dd7a00' }}>
                          {c.resolution || '未解決'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Timeline */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>
                <MdHistory style={{ verticalAlign: 'middle', marginRight: 4 }} />
                学習履歴 Timeline ({detail.timeline.length} 件)
              </SubSectionTitle>
              {detail.timeline.length === 0 ? (
                <Note mt={0.25}>履歴なし</Note>
              ) : (
                <div style={{ marginTop: '4px', maxHeight: '320px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>at</th>
                        <th style={th}>type</th>
                        <th style={th}>template_id</th>
                        <th style={th}>detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.timeline.slice(0, 50).map((ev, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(ev.at)}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: ev.type.includes('promoted') ? '#338650' :
                                   ev.type.includes('demoted') || ev.type.includes('reject') || ev.type.includes('drift') ? '#c33939' :
                                   ev.type.includes('repromote') ? '#2e7cf6' : '#7c5cff' }}>
                            {ev.type}
                          </td>
                          <td style={td}>{ev.template_id || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{ev.detail || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Outcomes (prefill 効果) */}
            <div style={{ marginTop: '16px' }}>
              <SubSectionTitle headlineLevel={4}>
                <MdRule style={{ verticalAlign: 'middle', marginRight: 4 }} />
                prefill 結果ログ (invoice_outcome_log, 直近 {detail.outcomes.length} 件)
              </SubSectionTitle>
              {detail.outcomes.length === 0 ? (
                <Note mt={0.25}>該当なし</Note>
              ) : (
                <div style={{ marginTop: '4px', overflowX: 'auto', maxHeight: '280px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                        <th style={th}>at</th>
                        <th style={th}>outcome</th>
                        <th style={th}>emitted?</th>
                        <th style={th}>used?</th>
                        <th style={th}>match?</th>
                        <th style={th}>source</th>
                        <th style={{ ...th, textAlign: 'right' }}>account_item</th>
                        <th style={th}>modified_fields</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.outcomes.map(o => (
                        <tr key={o.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(o.created_at)}</td>
                          <td style={{ ...td, fontWeight: 600,
                            color: o.outcome === 'approved_as_is' ? '#338650' :
                                   o.outcome === 'approved_with_modification' ? '#dd7a00' :
                                   o.outcome === 'rejected' ? '#c33939' : '#8a8580' }}>
                            {o.outcome}
                          </td>
                          <td style={td}>{o.prefill_emitted ? '✓' : '—'}</td>
                          <td style={td}>{o.prefill_used ? '✓' : '—'}</td>
                          <td style={td}>{o.prefill_match === 1 ? '✓' : o.prefill_match === 0 ? '✗' : '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem' }}>{o.recommend_source || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{o.final_account_item_id || '—'}</td>
                          <td style={{ ...td, fontSize: '0.6875rem', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {o.modified_fields || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </CardBase>
  );
};

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '0.6875rem',
  color: '#6b6560',
  fontWeight: 500,
  position: 'sticky',
  top: 0,
  background: '#f7f5f5',
};
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ icon, label, value, sub, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ color, fontSize: '1rem', display: 'inline-flex' }}>{icon}</span>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: 600, color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{sub}</div>}
  </div>
);

const Row: React.FC<{ label: string; value: any; highlight?: string }> = ({ label, value, highlight }) => (
  <tr style={{ borderBottom: '1px solid #f0eeec' }}>
    <td style={{ padding: '6px 8px', color: '#6b6560', fontSize: '0.78rem' }}>{label}</td>
    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: highlight || '#332f2c' }}>{value}</td>
  </tr>
);

// P1-5 (2026-05-18) — TemplateActionButtons
//   PartnerStatusDrillDown の template 行に approve / reject / promote button を追加。
//   approve → /api/admin/invoice-layout-template-promote (force_promote)
//   reject  → /api/admin/invoice-layout-outcome (outcome='reject')
//   demote  → /api/admin/invoice-layout-template-promote (force_demote)
const TemplateActionButtons: React.FC<{
  templateId: number;
  status: string;
  onChange: () => void;
}> = ({ templateId, status, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const HUB = 'https://invoice-review.system-0f7.workers.dev';

  const post = useCallback(async (path: string, body: any, label: string) => {
    if (!window.confirm(`${label} を実行しますか? (template id=${templateId})`)) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${HUB}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${r.status}`);
      }
      onChange();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [templateId, onChange]);

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {status !== 'promoted' && (
        <button
          disabled={busy}
          onClick={() => post('/api/admin/invoice-layout-template-promote',
            { template_id: templateId, force: true, reason: 'manual_promote_from_partner_drilldown' },
            'force promote')}
          style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer', background: '#338650', color: 'white', border: 'none', borderRadius: 3 }}
        >promote</button>
      )}
      {status === 'promoted' && (
        <button
          disabled={busy}
          onClick={() => post('/api/admin/invoice-layout-template-promote',
            { template_id: templateId, force_demote: true, reason: 'manual_demote_from_partner_drilldown' },
            'force demote')}
          style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer', background: '#dd7a00', color: 'white', border: 'none', borderRadius: 3 }}
        >demote</button>
      )}
      {err && <span style={{ fontSize: 10, color: '#c33939' }}>{err}</span>}
    </div>
  );
};
